import type { RouteApi } from '../useRoute'
import type { AdminSport, AdminSportsbook, AdminSubAffiliate } from '../admin/api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AdminAuthError,
  fetchAdminSports,
  fetchAdminSportsbooks,
  fetchSubAffiliates,
} from '../admin/api'
import { assetUrl, categoryLabel, GROUP_ORDER } from '../api'
import { SportSheet, SportSheetBar, useSportSheetOpener } from '../SportSheet'
import { useResizable } from '../useResizable'
import AffiliateOperatorPage from './AffiliateOperatorPage'
import AffiliateSportPage from './AffiliateSportPage'
import SubAffiliatePage from './SubAffiliatePage'

/**
 * /affiliate — owner-only home for all things affiliate, structurally a
 * sibling of /distribution (same tabs-over-sidebar-over-editor chrome, same
 * sports + sportsbooks catalogs) with three sub-tabs:
 *
 *   SPORTS         → /affiliate/sports/{slug}      (carriers, URLs, offers)
 *   OPERATORS      → /affiliate/operators/{slug}   (brand, links by sport, offers)
 *   SUB-AFFILIATES → /affiliate/subs/{flag}        (migration 0059 — internal
 *                    records: promoted sports, per-book links, embed flag)
 *
 * Read-only v1 except the offers panel. main.tsx gates the route to owner;
 * the Header hides the nav link from everyone else.
 */

interface AffiliateAppProps {
  route: RouteApi
}


const ACCORDION_STORAGE_KEY = 'odds-portal-affiliate-accordion'

function readInitialExpanded(): Set<string> {
  if (typeof window === 'undefined')
    return new Set(['MOTORSPORT'])
  try {
    const stored = localStorage.getItem(ACCORDION_STORAGE_KEY)
    if (stored != null) {
      const parsed = JSON.parse(stored) as unknown
      // Single-open accordion — sessions stored before the multi-open
      // behavior was retired may hold several categories; keep the first.
      if (Array.isArray(parsed))
        return new Set(parsed.filter(x => typeof x === 'string').slice(0, 1))
    }
  }
  catch {
    /* ignore */
  }
  return new Set(['MOTORSPORT'])
}

type Tab = 'sports' | 'operators' | 'subs'
type AffiliateRoute
  = | { tab: 'sports', sportSlug?: string }
    | { tab: 'operators', operatorSlug?: string }
    | { tab: 'subs', subSlug?: string }

function parseAffiliateRoute(path: string): AffiliateRoute {
  const parts = path.toLowerCase().split('/').filter(Boolean)
  if (parts[1] === 'sports')
    return { tab: 'sports', sportSlug: parts[2] }
  if (parts[1] === 'operators')
    return { tab: 'operators', operatorSlug: parts[2] }
  if (parts[1] === 'subs')
    return { tab: 'subs', subSlug: parts[2] }
  return { tab: 'sports' }
}

function slugifySportsbook(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export default function AffiliateApp({ route }: AffiliateAppProps) {
  const [tab, setTab] = useState<Tab>('sports')
  const [sports, setSports] = useState<AdminSport[] | null>(null)
  const [sportsbooks, setSportsbooks] = useState<AdminSportsbook[] | null>(null)
  const [subs, setSubs] = useState<AdminSubAffiliate[] | null>(null)
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null)
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null)
  const [selectedSubId, setSelectedSubId] = useState<number | null>(null)
  // True while the SUB-AFFILIATES pane shows the blank create form.
  const [creatingSub, setCreatingSub] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Mobile sheet — openable from the sticky switcher bar and the header
  // menu's SPORTS row (useSportSheetOpener bus).
  const [sportSheetOpen, setSportSheetOpen] = useState(false)
  useSportSheetOpener(useCallback(() => setSportSheetOpen(true), []))

  const sidebar = useResizable({
    defaultWidth: 280,
    minWidth: 200,
    maxWidth: 600,
    storageKey: 'odds-portal-affiliate-sidebar-width',
  })

  const [searchSports, setSearchSports] = useState('')
  const [searchBooks, setSearchBooks] = useState('')
  const [searchSubs, setSearchSubs] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(readInitialExpanded)

  function toggleExpanded(cat: string) {
    setExpanded((prev) => {
      // Single-open accordion: opening a group closes whichever was open;
      // clicking the open group closes it (all-closed is a valid state).
      const next = new Set(prev.has(cat) ? [] : [cat])
      try {
        localStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify([...next]))
      }
      catch {
        /* ignore */
      }
      return next
    })
  }

  const affiliateRoute = useMemo(
    () => parseAffiliateRoute(route.path),
    [route.path],
  )

  useEffect(() => {
    setTab(affiliateRoute.tab)
  }, [affiliateRoute.tab])

  function refetchAll() {
    Promise.all([fetchAdminSports(), fetchAdminSportsbooks(), fetchSubAffiliates()])
      .then(([s, b, a]) => {
        setSports(s)
        setSportsbooks(b)
        setSubs(a)
        setSelectedSportId(prev => prev ?? s[0]?.id ?? null)
        setSelectedBookId(prev => prev ?? b[0]?.id ?? null)
        setSelectedSubId(prev => (prev != null && a.some(x => x.id === prev)) ? prev : a[0]?.id ?? null)
      })
      .catch((e) => {
        if (e instanceof AdminAuthError) {
          window.location.reload()
          return
        }
        setError((e as Error).message)
      })
  }

  useEffect(() => {
    refetchAll()
  }, [])

  const selectedSport = sports?.find(s => s.id === selectedSportId) ?? null
  const selectedBook = sportsbooks?.find(b => b.id === selectedBookId) ?? null
  const selectedSub = subs?.find(a => a.id === selectedSubId) ?? null

  useEffect(() => {
    if (affiliateRoute.tab !== 'sports' || !sports || !('sportSlug' in affiliateRoute) || !affiliateRoute.sportSlug)
      return
    const match = sports.find(s => s.slug?.toLowerCase() === affiliateRoute.sportSlug)
    if (match)
      setSelectedSportId(match.id)
  }, [affiliateRoute, sports])

  useEffect(() => {
    if (affiliateRoute.tab !== 'operators' || !sportsbooks || !('operatorSlug' in affiliateRoute) || !affiliateRoute.operatorSlug)
      return
    const match = sportsbooks.find(b => slugifySportsbook(b.name) === affiliateRoute.operatorSlug)
    if (match)
      setSelectedBookId(match.id)
  }, [affiliateRoute, sportsbooks])

  useEffect(() => {
    if (affiliateRoute.tab !== 'subs' || !subs || !('subSlug' in affiliateRoute) || !affiliateRoute.subSlug)
      return
    const match = subs.find(a => a.slug === affiliateRoute.subSlug)
    if (match) {
      setSelectedSubId(match.id)
      setCreatingSub(false)
    }
  }, [affiliateRoute, subs])

  const groupedSports = useMemo(() => {
    if (!sports)
      return new Map<string, AdminSport[]>()
    const q = searchSports.trim().toLowerCase()
    const groups = new Map<string, AdminSport[]>()
    for (const cat of GROUP_ORDER) groups.set(cat, [])
    for (const s of sports) {
      if (q && !s.name.toLowerCase().includes(q) && !(s.slug ?? '').toLowerCase().includes(q)) {
        continue
      }
      const label = categoryLabel(s.category)
      if (!groups.has(label))
        groups.set(label, [])
      groups.get(label)!.push(s)
    }
    for (const [k, v] of groups) {
      if (v.length === 0)
        groups.delete(k)
      else v.sort((a, b) => a.name.localeCompare(b.name))
    }
    return groups
  }, [sports, searchSports])

  const isSearching = searchSports.trim().length > 0
  const effectiveExpanded = isSearching
    ? new Set(groupedSports.keys())
    : expanded

  const filteredSportsbooks = useMemo(() => {
    if (!sportsbooks)
      return null
    const q = searchBooks.trim().toLowerCase()
    const matched = q
      ? sportsbooks.filter(b => b.name.toLowerCase().includes(q))
      : sportsbooks
    const byName = (a: AdminSportsbook, b: AdminSportsbook) => a.name.localeCompare(b.name)
    const active = [...matched].sort(byName)
    return { active, total: active.length }
  }, [sportsbooks, searchBooks])

  const filteredSubs = useMemo(() => {
    if (!subs)
      return null
    const q = searchSubs.trim().toLowerCase()
    return q
      ? subs.filter(a => a.name.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q))
      : subs
  }, [subs, searchSubs])

  function routeForTab(nextTab: Tab): string {
    if (nextTab === 'sports' && selectedSport?.slug) {
      return `/affiliate/sports/${selectedSport.slug.toLowerCase()}`
    }
    if (nextTab === 'operators' && selectedBook) {
      return `/affiliate/operators/${slugifySportsbook(selectedBook.name)}`
    }
    if (nextTab === 'subs') {
      return selectedSub ? `/affiliate/subs/${selectedSub.slug}` : '/affiliate/subs'
    }
    return '/affiliate'
  }

  // Mobile sheet pick — mirrors the sidebar rows' navigation exactly.
  function sheetSelect(key: string) {
    setSportSheetOpen(false)
    if (tab === 'operators') {
      const b = sportsbooks?.find(x => String(x.id) === key)
      if (b)
        route.navigate(`/affiliate/operators/${slugifySportsbook(b.name)}`)
      return
    }
    if (tab === 'subs') {
      const a = subs?.find(x => String(x.id) === key)
      if (a)
        route.navigate(`/affiliate/subs/${a.slug}`)
      return
    }
    const s = sports?.find(x => String(x.id) === key)
    if (!s)
      return
    if (s.slug)
      route.navigate(`/affiliate/sports/${s.slug.toLowerCase()}`)
    else setSelectedSportId(s.id)
  }

  return (
    <div className="ad-root">
      <nav className="ad-tabs">
        {([
          { id: 'sports', label: `SPORTS · ${sports?.length ?? '—'}` },
          { id: 'operators', label: `OPERATORS · ${sportsbooks?.length ?? '—'}` },
          { id: 'subs', label: `SUB-AFFILIATES · ${subs?.length ?? '—'}` },
        ] as const).map(t => (
          <button
            type="button"
            key={t.id}
            onClick={() => {
              route.navigate(routeForTab(t.id))
            }}
            className={`b-tab${tab === t.id ? ' is-active' : ''}`}
          >
            <span className="b-mono-dim b-tiny">
              [
              {tab === t.id ? '◼' : ' '}
              ]
            </span>
            {t.label}
          </button>
        ))}
      </nav>

      {error && (
        <p className="b-mono ad-error">
          ⚠
          {error}
        </p>
      )}

      <main className="ad-main">
        {/* Mobile: the sidebar hides (bm-hide-mobile); this sticky bar +
            the shared SportSheet take over switching. */}
        <SportSheetBar
          label={tab === 'operators'
            ? selectedBook?.name ?? null
            : tab === 'subs'
              ? selectedSub?.name ?? null
              : selectedSport?.name ?? null}
          placeholder={tab === 'operators' ? 'SELECT OPERATOR' : tab === 'subs' ? 'SELECT SUB-AFFILIATE' : 'SELECT SPORT'}
          onOpen={() => setSportSheetOpen(true)}
        />
        {tab === 'sports' && (
          <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
            <aside className="ad-list-pane bm-hide-mobile">
              <div className="ad-list-search">
                <span className="b-mono-dim">/</span>
                <input
                  value={searchSports}
                  onChange={e => setSearchSports(e.target.value)}
                  placeholder="filter sports…"
                  className="ad-list-search-input"
                  spellCheck={false}
                />
                {searchSports && (
                  <button
                    type="button"
                    onClick={() => setSearchSports('')}
                    className="ad-list-search-clear"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {!sports
                ? <p className="b-mono-dim">LOADING…</p>
                : groupedSports.size === 0
                  ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
                  : [...groupedSports.entries()].map(([cat, items]) => {
                      const open = effectiveExpanded.has(cat)
                      return (
                        <div key={cat} className={`ad-list-group${open ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            onClick={() => toggleExpanded(cat)}
                            className="ad-list-group-head"
                            aria-expanded={open}
                          >
                            <span className="ad-list-group-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                            <span className="ad-list-group-label">{cat}</span>
                            <span className="ad-list-group-count">{items.length}</span>
                          </button>
                          {open && items.map((s) => {
                            const active = s.id === selectedSportId
                            return (
                              <button
                                type="button"
                                key={s.id}
                                onClick={() => {
                                  if (s.slug)
                                    route.navigate(`/affiliate/sports/${s.slug.toLowerCase()}`)
                                  else
                                    setSelectedSportId(s.id)
                                }}
                                className={`ad-list-row${active ? ' is-active' : ''}`}
                              >
                                {s.logo_url && (
                                  <img
                                    src={assetUrl(s.logo_url)}
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                    className="ad-list-logo"
                                  />
                                )}
                                <span className="ad-list-name">
                                  {s.name}
                                  {s.slug && (
                                    <span className="b-mono-dim ad-list-slug">
                                      {' '}
                                      ·
                                      {s.slug}
                                    </span>
                                  )}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      )
                    })}
              <button
                type="button"
                className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
                onMouseDown={sidebar.onMouseDown}
                aria-label="Resize sidebar"
              />
            </aside>
            <div className="ad-edit-pane">
              {selectedSport && sportsbooks && (
                <AffiliateSportPage
                  key={selectedSport.id}
                  sport={selectedSport}
                  sportsbooks={sportsbooks}
                  onSaved={() => { refetchAll() }}
                />
              )}
            </div>
          </div>
        )}

        {tab === 'operators' && (
          <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
            <aside className="ad-list-pane bm-hide-mobile">
              <div className="ad-list-search">
                <span className="b-mono-dim">/</span>
                <input
                  value={searchBooks}
                  onChange={e => setSearchBooks(e.target.value)}
                  placeholder="filter operators…"
                  className="ad-list-search-input"
                  spellCheck={false}
                />
                {searchBooks && (
                  <button
                    type="button"
                    onClick={() => setSearchBooks('')}
                    className="ad-list-search-clear"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {!filteredSportsbooks
                ? <p className="b-mono-dim">LOADING…</p>
                : filteredSportsbooks.total === 0
                  ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
                  : (
                      <>
                        {filteredSportsbooks.active.map((b) => {
                          const active = b.id === selectedBookId
                          return (
                            <button
                              type="button"
                              key={b.id}
                              onClick={() => route.navigate(`/affiliate/operators/${slugifySportsbook(b.name)}`)}
                              className={`ad-list-row${active ? ' is-active' : ''}`}
                            >
                              <span className="ad-list-name">{b.name}</span>
                            </button>
                          )
                        })}
                      </>
                    )}
              <button
                type="button"
                className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
                onMouseDown={sidebar.onMouseDown}
                aria-label="Resize sidebar"
              />
            </aside>
            <div className="ad-edit-pane">
              {selectedBook && sports && (
                <AffiliateOperatorPage
                  key={selectedBook.id}
                  book={selectedBook}
                  sports={sports}
                  onSaved={() => { refetchAll() }}
                />
              )}
            </div>
          </div>
        )}

        {tab === 'subs' && (
          <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
            <aside className="ad-list-pane bm-hide-mobile">
              <div className="ad-list-search">
                <span className="b-mono-dim">/</span>
                <input
                  value={searchSubs}
                  onChange={e => setSearchSubs(e.target.value)}
                  placeholder="filter sub-affiliates…"
                  className="ad-list-search-input"
                  spellCheck={false}
                />
                {searchSubs && (
                  <button
                    type="button"
                    onClick={() => setSearchSubs('')}
                    className="ad-list-search-clear"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCreatingSub(true)}
                  className="b-btn b-btn-primary ad-list-new-btn"
                >
                  + NEW
                </button>
              </div>
              {!filteredSubs
                ? <p className="b-mono-dim">LOADING…</p>
                : filteredSubs.length === 0
                  ? <p className="b-mono-dim ad-list-empty-search">{searchSubs.trim() ? 'no matches' : 'none yet'}</p>
                  : filteredSubs.map((a) => {
                      const active = !creatingSub && a.id === selectedSubId
                      return (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => {
                            setCreatingSub(false)
                            route.navigate(`/affiliate/subs/${a.slug}`)
                          }}
                          className={`ad-list-row${active ? ' is-active' : ''}`}
                        >
                          <span className="ad-list-name">
                            {a.name}
                            <span className="b-mono-dim ad-list-slug">
                              {' '}
                              ·
                              {a.slug}
                            </span>
                          </span>
                        </button>
                      )
                    })}
              <button
                type="button"
                className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
                onMouseDown={sidebar.onMouseDown}
                aria-label="Resize sidebar"
              />
            </aside>
            <div className="ad-edit-pane">
              {sports && sportsbooks && (creatingSub || selectedSub) && (
                <SubAffiliatePage
                  key={creatingSub ? 'new' : selectedSub!.id}
                  sub={creatingSub ? null : selectedSub}
                  sports={sports}
                  sportsbooks={sportsbooks}
                  onSaved={(slug) => {
                    setCreatingSub(false)
                    refetchAll()
                    route.navigate(`/affiliate/subs/${slug}`)
                  }}
                  onDeleted={() => {
                    setSelectedSubId(null)
                    refetchAll()
                    route.navigate('/affiliate/subs')
                  }}
                />
              )}
              {sports && sportsbooks && !creatingSub && !selectedSub && (
                <p className="b-mono-dim" style={{ padding: 16 }}>
                  no sub-affiliates yet — create the first with + NEW
                </p>
              )}
            </div>
          </div>
        )}
      </main>

      {sportSheetOpen && (
        <SportSheet
          title={tab === 'operators' ? 'SELECT OPERATOR' : tab === 'subs' ? 'SELECT SUB-AFFILIATE' : 'SELECT SPORT'}
          items={tab === 'operators'
            ? (sportsbooks ?? []).map(b => ({ key: String(b.id), label: b.name }))
            : tab === 'subs'
              ? (subs ?? []).map(a => ({ key: String(a.id), label: a.name, sub: a.slug }))
              : (sports ?? []).map(s => ({
                  key: String(s.id),
                  label: s.name,
                  category: s.category,
                  sub: s.slug ?? undefined,
                }))}
          selectedKey={tab === 'operators'
            ? (selectedBookId != null ? String(selectedBookId) : null)
            : tab === 'subs'
              ? (selectedSubId != null ? String(selectedSubId) : null)
              : (selectedSportId != null ? String(selectedSportId) : null)}
          onSelect={sheetSelect}
          onClose={() => setSportSheetOpen(false)}
        />
      )}
    </div>
  )
}
